import type { Message, ToolCall as AgUiToolCall } from '@ag-ui/client';
import type { ChatError, ChatMessage, ToolCallView } from '../types';
import type { ChatRuntimeSnapshot } from './types';

/**
 * Pure projection of an AG-UI message array (plus run-lifecycle metadata)
 * into a `ChatRuntimeSnapshot`. Intentionally side-effect-free and free of
 * any CopilotKit/AG-UI runtime dependency — only the message *types* are
 * imported, so the file can be unit-tested in isolation.
 *
 * Mapping table mirrors §2 of the v4 plan:
 *  - `UserMessage`                                       → user bubble
 *  - `AssistantMessage` (not last)                       → assistant bubble
 *  - `AssistantMessage` (last while streaming)           → status='streaming'
 *  - `AssistantMessage` (last, finalized)                → status='sent'
 *  - `ToolCall` on AssistantMessage with no `ToolMessage` → ToolCallView running
 *  - `ToolCall` paired with `ToolMessage` (no error)     → ToolCallView done
 *  - `ToolCall` paired with `ToolMessage` (error string) → ToolCallView error
 *  - `RunFailed` event (lifted into `lastError`)         → snapshot.lastError
 * @param messages - Reactive AG-UI messages from `agent.messages`.
 * @param phase - Run phase: 'idle' when no run active, 'thinking' before first text token, 'streaming' once text begins.
 * @param runId - Active run id, or null when no run is in flight.
 * @param lastError - Most recent terminal error, or null.
 * @param agentState - Mirrored AG-UI agent state, untouched.
 * @returns A `ChatRuntimeSnapshot` shaped for the Vantage UI components.
 */
export const toAgUiSnapshot = (
  messages: ReadonlyArray<Readonly<Message>>,
  phase: 'idle' | 'thinking' | 'streaming',
  runId: string | null,
  lastError: ChatError | null,
  agentState: unknown
): ChatRuntimeSnapshot => {
  const isStreaming = phase !== 'idle';
  // Build a quick lookup of tool-result content keyed by toolCallId. AG-UI
  // ships tool results as separate messages with `role: 'tool'` referencing
  // the original toolCallId — pair them here so the UI shows resolved chips
  // even after the assistant message has already streamed past them.
  const toolResultsById = new Map<string, { content: string; error?: string }>();
  for (const message of messages) {
    if (message.role === 'tool') {
      toolResultsById.set(message.toolCallId, {
        content: message.content,
        error: message.error
      });
    }
  }

  const projected: ChatMessage[] = [];
  // Track the last assistant message so we can flip it to 'streaming' when
  // the run is still in flight; everything earlier is implicitly 'sent'.
  let lastAssistantIndex = -1;

  for (const message of messages) {
    if (message.role === 'user') {
      const text = typeof message.content === 'string' ? message.content : extractUserMessageText(message.content);
      projected.push({
        id: message.id,
        role: 'user',
        text,
        createdAt: new Date().toISOString(),
        status: 'sent'
      });
      continue;
    }

    if (message.role === 'assistant') {
      const toolCalls = projectToolCalls(message.toolCalls, toolResultsById);
      projected.push({
        id: message.id,
        role: 'assistant',
        text: message.content ?? '',
        createdAt: new Date().toISOString(),
        status: 'sent',
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined
      });
      lastAssistantIndex = projected.length - 1;
      continue;
    }

    // Tool, system, developer, reasoning, activity messages are intentionally
    // not rendered as standalone bubbles; tool results are folded into the
    // owning assistant message above. Future phases can extend this.
  }

  // Use phase directly here (not isStreaming) — during 'thinking' no new
  // assistant message exists yet, so lastAssistantIndex points to the prior
  // turn's complete bubble. Flipping that to 'streaming' is the bug.
  if (phase === 'streaming' && lastAssistantIndex >= 0) {
    const tail = projected[lastAssistantIndex];
    projected[lastAssistantIndex] = { ...tail, status: 'streaming' };
  }

  return {
    messages: projected,
    isStreaming,
    phase,
    runId,
    lastError,
    agentState
  };
};

const extractUserMessageText = (content: ReadonlyArray<{ type: string; text?: string } | unknown>): string => {
  // AG-UI's UserMessage allows multimodal content; the Vantage UI only
  // renders plain text in Phase 2, so we pull out text parts and ignore
  // images/audio/etc. without crashing the projection.
  const parts: string[] = [];
  for (const part of content) {
    if (part && typeof part === 'object' && 'type' in part && (part as { type: string }).type === 'text') {
      const textPart = part as { text?: string };
      if (typeof textPart.text === 'string') parts.push(textPart.text);
    }
  }
  return parts.join('');
};

const parseArguments = (raw: string | undefined): unknown => {
  // AG-UI streams tool arguments as a JSON string on `ToolCall.function.arguments`.
  // The string can be empty or partial during inProgress; only attempt JSON.parse
  // when the trimmed payload looks like a complete object/array literal.
  if (raw === undefined || raw === null) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return raw;
    }
  }
  return raw;
};

const projectToolCalls = (
  toolCalls: AgUiToolCall[] | undefined,
  toolResultsById: Map<string, { content: string; error?: string }>
): ToolCallView[] => {
  if (!toolCalls || toolCalls.length === 0) return [];
  return toolCalls.map((toolCall) => {
    const args = parseArguments(toolCall.function.arguments);
    const result = toolResultsById.get(toolCall.id);
    if (!result) {
      return {
        id: toolCall.id,
        name: toolCall.function.name,
        status: 'running',
        args
      };
    }
    if (result.error) {
      return {
        id: toolCall.id,
        name: toolCall.function.name,
        status: 'error',
        errorMessage: result.error,
        resultSummary: result.content,
        args
      };
    }
    return {
      id: toolCall.id,
      name: toolCall.function.name,
      status: 'done',
      resultSummary: result.content,
      args
    };
  });
};
